import {
  computed,
  onBeforeMount,
  onUnmounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useDocumentVisibility } from "@vueuse/core";

import { PSD2_TYPE } from "~/utils/const";
import { Track } from "~/utils/gtag";
import {
  GO,
  PREVIOUS,
  PSD2_STATE,
  isSyncInProgress,
} from "~/utils/psd2/constants";
import { Poller } from "~/utils/psd2/Poller";
import { SYNC_IN_PROGRESS, presentationFor } from "~/utils/psd2/status";
import {
  INITIAL_STATE_ID,
  START_STATE_ID,
  actionAreaFor,
  resolveState,
  stateAfter,
  stateBefore,
} from "~/utils/psd2/states";
import { createPsd2Aggregator } from "~/utils/psd2/aggregators";
import { createSyncMarker } from "~/utils/psd2/SyncMarker";
import { psd2Token } from "~/utils/psd2/tokens";

import { useChatStore } from "~/stores/chat";
import { useConfigStore } from "~/stores/config";
import { useCountryStore } from "~/stores/country";
import { usePsd2FlowStore } from "~/stores/psd2Flow";
import {
  usePsd2BusinessGocaStore,
  usePsd2GocaStore,
} from "~/stores/psd2GocaStore";
import { usePsd2BusinessSyncStore, usePsd2SyncStore } from "~/stores/psd2Sync";

const POLL_INTERVAL_MILLIS = 3000;
const POLL_TIMEOUT_MILLIS = 5 * 60 * 1000;

/**
 * Everything the PSD2 screens do, so the components themselves are only markup.
 *
 * The pieces are deliberately separable: which screens exist and what each one
 * does is states.js, which aggregator we talk to is one entry in the table in
 * aggregators.js, what a status means is status.js, and knowing a sync survived
 * a page load is a `SyncMarker`. This composable is the only place that knows
 * about all of them, and the only place that touches reactive state.
 *
 * Where the flow leads is nobody's business here: these screens emit `done` and
 * `back`, and the page that mounted them moves its own flow. That used to be a
 * hierarchy of navigator classes reaching into six host stores from a distance,
 * which meant the answer to "what does next do here" lived two files away from
 * the mount that decided it.
 */
export function usePsd2Flow(props, emit) {
  const psd2FlowStore = usePsd2FlowStore();
  const countryStore = useCountryStore();
  const configStore = useConfigStore();
  const chatStore = useChatStore();

  // Fixed for the life of this component: a mount does not change segment
  // without being replaced, and the stores it talks to are chosen once.
  const isBusiness = props.isBusiness;

  // Both pairs are resolved because a Pinia store is cheap and which pair is
  // in play is the segment's answer, not this function's.
  const a8Store = isBusiness ? usePsd2BusinessSyncStore() : usePsd2SyncStore();
  const goCardlessStore = isBusiness
    ? usePsd2BusinessGocaStore()
    : usePsd2GocaStore();

  // Resolved on access, not once: the country config can land after mount,
  // and until it does we would otherwise be locked to the wrong aggregator.
  const provider = computed(() => {
    let psd2Type = isBusiness
      ? countryStore.businessSyncType
      : countryStore.psd2Type;
    return createPsd2Aggregator(psd2Type, {
      store: psd2Type === PSD2_TYPE.GO_CARDLESS ? goCardlessStore : a8Store,
      isBusiness: isBusiness,
    });
  });

  /**
   * Who this sync is for. The mount site names it, so nothing is
   * reconstructed from the route - which used to produce two answers to
   * "whose sync is this" that could disagree.
   */
  const subject = computed(() => ({
    publicId: props.publicId,
    prescoreId: props.prescoreId,
    companyId: props.companyId,
  }));

  /** What the surrounding page allows, which only it knows. */
  const host = computed(() => ({
    ownsActionArea: props.ownsActionArea,
    canLeaveBackwards: props.canLeaveBackwards,
    canStepBackwards: props.canStepBackwards,
  }));

  const marker = createSyncMarker();

  /**
   * The id of the sync we handed to the bank, mirrored out of the marker so
   * the screens can react to it. A8 needs it to close a sync, and it is only
   * ever handed out by the start call - so this note is the one place it
   * survives the page load that starting a sync causes.
   */
  const inFlightSyncId = ref(marker.read()?.syncId ?? null);

  function rememberStartedSync(syncId) {
    marker.mark({ syncId: syncId, source: props.source });
    inFlightSyncId.value = syncId ?? null;
  }

  function forgetStartedSync() {
    marker.clear();
    inFlightSyncId.value = null;
  }

  const stateId = ref(INITIAL_STATE_ID);
  const syncStarted = ref(false);
  const bankSelect = ref(null);

  const state = computed(() => resolveState(stateId.value));
  const latestSync = computed(() => provider.value.latest);

  // One bank list serves both aggregators, and a bank the configured one
  // cannot reach must not be offered.
  const banks = computed(() => {
    let aggregator = provider.value;
    return psd2FlowStore.banks.filter((bank) => aggregator.canSync(bank));
  });

  /**
   * The in-progress screen's buttons, which depend on what the aggregator
   * supports rather than on which aggregator it is.
   */
  const progress = computed(() => ({
    // Some aggregators can only close a sync they were told the id of, and
    // we only have that while the start note is still around.
    canCancel:
      provider.value.supportsCancel &&
      (!provider.value.cancelNeedsSyncId || !!inFlightSyncId.value),
    showResume: provider.value.canResume,
    showRefresh: !provider.value.canResume,
  }));

  // -- moving between states ----------------------------------------------

  /**
   * Normalised on the way in, so `stateId` only ever names a state that
   * exists. One that has since been deleted lands on the fallback here,
   * rather than being stored and resolved differently by every reader.
   */
  function goTo(id) {
    stateId.value = resolveState(id).id;
  }

  /** Reported outcomes, so polling does not re-report the same one. */
  let notifiedEvent = null;

  function present(presentation) {
    if (!presentation) {
      // We know nothing new. Leaving the customer where they are beats
      // guessing, and guessing has historically meant the intro screen.
      return;
    }

    if (presentation.resetsNotifications) {
      notifiedEvent = null;
    }

    goTo(presentation.state);

    // Whether an outcome ends the sync is a fact about the screen, not
    // about the status that led there, so the registry is asked.
    if (state.value.terminal) {
      forgetStartedSync();
    }

    if (presentation.event && notifiedEvent !== presentation.event) {
      notifiedEvent = presentation.event;
      Track.event(presentation.event);
      if (presentation.emitsSynced) {
        emit("synced");
      }
    }

    // A state that hands the flow on by itself does not wait for a click.
    // `displayState` below substitutes the loading screen for it on this
    // same tick, so nothing it would have shown ever becomes visible.
    if (state.value.autoAdvances(flow)) {
      nextStep();
    }
  }

  /**
   * Read from the props each time rather than captured once: a todo's
   * creation date can arrive after this component has already mounted, and a
   * stale one silently changes which syncs count as answering it.
   */
  function applyStatus() {
    present(
      presentationFor(latestSync.value, {
        source: props.source,
        correctionCreatedAt: props.correctionCreatedAt,
        isBusiness: isBusiness,
        syncInFlight: marker.isActiveFor(props.source),
      })
    );
  }

  // -- talking to the backend ---------------------------------------------

  async function refreshStatus(force) {
    // Everything an aggregator might need. Which parts of it reach the
    // endpoint is the aggregator's answer, not ours.
    return await provider.value.fetchStatus(force, {
      ...subject.value,
      flowId: props.flowId,
    });
  }

  const poller = new Poller({
    intervalMillis: POLL_INTERVAL_MILLIS,
    timeoutMillis: POLL_TIMEOUT_MILLIS,
    onTick: async () => {
      let latest = await refreshStatus(false);
      applyStatus();
      return isSyncInProgress(latest?.lastSyncStatus);
    },
    onError: (e) => console.log("PSD2:: could not poll sync status", e),
    onTimeout: () =>
      console.log("PSD2:: polling timed out, leaving the manual refresh"),
  });

  function pollWhileRunning() {
    if (state.value.polls && !poller.isRunning) {
      poller.start();
    }
  }

  /** The manual refresh button. "yes" makes the backend re-read the aggregator. */
  async function forceRefresh() {
    try {
      await refreshStatus("yes");
      applyStatus();
    } catch (e) {
      console.log("PSD2:: could not refresh sync status", e);
    }
  }

  // -- actions ------------------------------------------------------------

  async function startSync() {
    let request = provider.value.buildStartRequest({
      source: props.source,
      correctionId: props.correctionId,
      fieldId: props.fieldId,
      redirectUrl: new URL(location.href),
      bank: bankSelect.value,
      countryId: countryStore.countryId,
      addBank: true,
      ...subject.value,
    });

    try {
      syncStarted.value = true;
      let startData = await provider.value.start(request);

      // The note has to be written before we navigate away: the next
      // thing that runs in this tab is a cold page load.
      rememberStartedSync(startData?.psd2SyncEntityId);

      window.location.assign(startData.userFlowUrl);
    } catch (e) {
      console.log("PSD2:: could not start the sync", e);
      forgetStartedSync();
      syncStarted.value = false;
      goTo(PSD2_STATE.FAILED);
    }
  }

  function resumeSync() {
    let url = provider.value.resumeUrl;
    if (url) {
      window.location.replace(url);
    }
  }

  /** Back to picking a bank, to start another sync. Where that is, the state says. */
  function restart() {
    goTo(state.value.restartsAt);
  }

  /**
   * The customer giving up on a sync they started. Leaves them where they
   * began it, ready to pick a bank again.
   *
   * A cancel that did not go through is not reported as one: the sync is
   * still running as far as anyone knows, so the screen stays put rather
   * than inviting a second attempt the backend would refuse.
   */
  async function cancelSync() {
    try {
      await provider.value.cancel({
        ...subject.value,
        syncId: inFlightSyncId.value,
      });
    } catch (e) {
      console.log("PSD2:: could not cancel the sync", e);
      return;
    }
    poller.stop();
    forgetStartedSync();
    restart();
  }

  /**
   * We are done with PSD2; the page that mounted us moves its own flow on.
   *
   * Guarded so a status re-reported while the host is still busy leaving -
   * a tab regaining focus mid hand-off, say - does not tell it twice.
   */
  let leftForward = false;
  function leaveForward() {
    if (leftForward) {
      return;
    }
    leftForward = true;
    emit("done");
  }

  /** The customer disputing the figures the sync produced. */
  async function reportMistake(request) {
    return await provider.value.mistakeFound(request);
  }

  /**
   * Where "next" leads is the state's own business. It answers with another
   * state, or with a sentinel when the answer is not a state at all.
   */
  async function nextStep() {
    let outcome = await state.value.next(flow);

    if (outcome === GO.STAY) {
      return;
    }
    if (outcome === GO.LEAVE) {
      leaveForward();
      return;
    }
    if (outcome === GO.NEXT) {
      let following = stateAfter(stateId.value);
      if (following) {
        goTo(following);
      }
      return;
    }
    goTo(outcome);
  }

  function backToPreviousStep() {
    if (state.value.previous === PREVIOUS.LEAVE) {
      emit("back");
      return;
    }

    let previous = stateBefore(stateId.value);
    if (previous) {
      goTo(previous);
    }
  }

  function selectBank(bank) {
    bankSelect.value = bank;
  }

  // -- wiring -------------------------------------------------------------

  watch(latestSync, applyStatus);

  // The cap is reported by the status call, at which point there is no status
  // left to show. Entering it as a state rather than drawing it over the
  // others keeps "what is on screen" a single answer.
  watch(
    () => provider.value.dailySyncLimitReached,
    (reached) => {
      if (reached) {
        goTo(PSD2_STATE.LIMIT_REACHED);
      }
    },
    { immediate: true }
  );

  watch(state, (current) => {
    chatStore.setFlowPosition(
      current.terminal ? "FLOW_PSD2_SUMMARY" : "FLOW_PSD2"
    );
  });

  /**
   * Bank flows routinely finish in another tab, or in the bank's own app with
   * a hop back through the browser. Coming back into view is often the first
   * moment we can learn the sync is done.
   */
  watch(useDocumentVisibility(), async (visibility) => {
    if (visibility !== "visible") {
      return;
    }
    try {
      await refreshStatus(false);
      applyStatus();
      pollWhileRunning();
    } catch (e) {
      console.log("PSD2:: could not refresh sync status on return", e);
    }
  });

  onBeforeMount(async () => {
    psd2FlowStore.fetchBanks(countryStore.locale.slice(0, 2), isBusiness);

    // Configs are nice to have. They must not be able to stop us finding
    // out where the customer actually stands.
    try {
      await configStore.fetchAutoloadConfigs();
    } catch (e) {
      console.log("PSD2:: could not load configs", e);
    }

    try {
      await refreshStatus(false);
      applyStatus();
    } catch (e) {
      // Not knowing the status is not the same as knowing there is no
      // sync, so the customer stays where they are - unless we have our
      // own note saying one is running.
      console.log("PSD2:: could not load sync status", e);
      if (marker.isActiveFor(props.source)) {
        present(SYNC_IN_PROGRESS);
      }
    } finally {
      // Whatever happened, we are no longer waiting to find out.
      if (state.value.initial) {
        goTo(START_STATE_ID);
      }
    }

    pollWhileRunning();
  });

  onUnmounted(() => poller.stop());

  /**
   * What the templates actually draw. A state that `autoAdvances` hands the
   * flow on the instant it is reached (see `present`), so it must never be
   * the one on screen even for the tick it takes the host to react - this
   * substitutes the loading screen for it. `state` itself stays the real
   * one: the composable's own bookkeeping (terminal, restartsAt, next) must
   * not be fooled by the substitution.
   */
  const displayState = computed(() =>
    state.value.autoAdvances(flow)
      ? resolveState(INITIAL_STATE_ID)
      : state.value
  );

  /**
   * Returned as one reactive object rather than a bag of refs: `reactive`
   * unwraps refs on read and write, so the state components take a single
   * `flow` prop and the state descriptors get one argument.
   *
   * Do not destructure this at the call site - that would take a snapshot and
   * lose reactivity.
   */
  const flow = reactive({
    // who we are
    isBusiness,
    /** Bare token keys in, namespaced keys out. */
    token: (key) => psd2Token(key, isBusiness),
    source: computed(() => props.source),
    skippable: computed(() => props.skippable),
    // where we are
    state: displayState,
    // what to show
    progress,
    sync: latestSync,
    banks,
    bankSelect,
    syncStarted,
    // what the customer can do
    next: nextStep,
    back: backToPreviousStep,
    skip: leaveForward,
    refresh: forceRefresh,
    resume: resumeSync,
    cancel: cancelSync,
    selectBank,
    startSync,
    restart,
    reportMistake,
    goTo,
  });

  // Added after the fact because it is the one thing here that reads the
  // whole context: the state's own predicates are handed `flow`.
  flow.actionArea = computed(() =>
    actionAreaFor(stateId.value, flow, host.value)
  );

  return flow;
}
